import type { PaymentRoutesTranslations } from "../interfaces/paymentRoutes";

export const paymentRoutesTranslations: PaymentRoutesTranslations = {
  // Page
  page_title: "Corridors & providers",
  page_subtitle:
    "Which corridors exist, and which provider and economic model runs each payin and payout. A corridor with no route refuses every operation.",
  refresh_button: "Refresh",
  refresh_aria: "Reload the corridors",
  button_new_route: "New route",

  // Loading states
  truncated_notice:
    "Loaded {{loaded}} of {{total}} routes. Coverage may be incomplete until all of them are loaded.",
  error_load: "The corridor catalog could not be loaded.",
  error_load_detail: "No rows are shown: corridor state is never made up.",
  error_retry: "Retry",

  // KPIs
  kpi_routed_label: "Routed corridors",
  kpi_routed_meta: "of {{total}} live corridors",
  kpi_missing_label: "No route",
  kpi_missing_meta: "Refuse every payin and payout",
  kpi_missing_ok: "Every corridor has a route",
  kpi_blocked_label: "Blocked",
  kpi_blocked_meta: "Inactive route holding the corridor",
  kpi_blocked_ok: "No inactive routes",
  kpi_unsupported_label: "Provider can't serve it",
  kpi_unsupported_meta: "Their payouts will stall on dispatch",
  kpi_unsupported_ok: "Every route has a capable provider",

  // Corridor states
  status_routed: "Routed",
  status_missing: "No route",
  status_blocked: "Blocked",
  status_unsupported: "Not covered",
  consequence_routed: "The corridor operates normally.",
  consequence_missing:
    "No route is configured: every payin and payout on this corridor is refused before pricing is even attempted.",
  consequence_blocked:
    "An inactive route is holding this corridor: it neither operates nor admits another route. It cannot be reactivated — delete it and create the new one.",
  consequence_unsupported:
    "The configured provider does not cover this corridor. Payouts reserve funds and then stall on dispatch, showing up in Payout rescue.",

  // Table
  th_corridor: "Corridor",
  th_direction: "Direction",
  th_status: "Status",
  th_model: "Economic model",
  th_provider: "Provider",
  provider_corridors: "Routes {{count}} corridor(s)",
  provider_unknown: "Unrecognised provider",
  provider_empty: "No provider",
  offcatalog_badge: "Off catalog",
  offcatalog_notice:
    "These routes point at corridors no dashboard flow can reach. They are shown because they exist in the configuration, not because they can be used.",

  // Actions
  action_view: "View",
  action_create: "Create route",
  action_switch: "Change provider",
  action_delete: "Delete route",

  // Empty & context
  empty_title: "No corridors configured",
  empty_description:
    "With no routes the payments engine refuses every payin and payout. Create the first route to enable a corridor.",
  why_banner:
    "Each route defines a corridor: without one there are no operations, and if it points at a provider that does not cover it, payouts stall with the funds already reserved.",

  // Side panel
  drawer_subtitle: "Detail of the route configured for this corridor.",
  drawer_section_corridor: "Corridor",
  drawer_section_provider: "Provider",
  drawer_section_audit: "Record",
  drawer_field_status: "Status",
  drawer_field_model: "Economic model",
  drawer_field_capability: "Capability for this corridor",
  drawer_field_id: "Route identifier",
  drawer_field_created: "Created",
  drawer_field_updated: "Updated",
  capability_ok: "The provider declares this corridor",
  capability_missing: "The provider does not declare this corridor",
  drawer_link_fees: "View this corridor's fees",
  drawer_copy_id: "Copy identifier",
  drawer_copied: "Identifier copied",

  // Create dialog
  create_title: "New route",
  create_subtitle: "Enable a corridor by choosing who runs it and under which economic model.",
  create_section_corridor: "Corridor",
  create_section_provider: "Provider",
  create_section_model: "Economic model",
  create_corridor_label: "Corridor",
  create_corridor_placeholder: "Pick a corridor",
  create_corridor_helper:
    "Rail and currency come from the corridor: the engine pins one of each per country.",
  create_direction_label: "Direction",
  direction_payin_description: "Money in: collections from clients.",
  direction_payout_description: "Money out: payouts to beneficiaries.",
  create_provider_label: "Who runs this corridor",
  create_provider_helper:
    "Only providers that declare this corridor are offered. One that does not cover it would leave payouts stalled.",
  provider_option_label: "Corridor provider",
  provider_option_coverage: "Covers: {{corridors}}",
  provider_option_meta: "Already routes {{count}} corridor(s)",
  provider_option_meta_none: "Does not route any corridor yet",
  provider_pick_corridor_first: "Pick the corridor and direction first to see which provider can run it.",
  create_provider_none_title: "No provider covers this corridor",
  create_provider_none_description:
    "No integrated provider can run it, so a route here would only leave operations stalled. It has to be integrated before the corridor can be enabled.",
  create_model_label: "Who pays the provider cost",
  create_model_helper: "Decides the net formula and the ledger legs of every operation on this corridor.",
  create_summary_title: "What will happen",
  create_summary_sentence: "Operations on {{corridor}} will run with {{model}}.",
  create_summary_pick_corridor: "Pick a corridor and a direction to see the outcome.",
  create_warn_exists:
    "This corridor already has a route. The engine allows exactly one per corridor: to reassign it, use “Change provider”.",
  create_warn_blocked:
    "This corridor has an inactive route holding it. That row has to be deleted before the new one can be created.",
  create_next_step:
    "The route is live immediately. Check that the corridor has fees configured: without them it also refuses operations.",
  submit: "Create route",
  cancel: "Cancel",
  validation_corridor_required: "Pick the corridor and the direction.",
  validation_provider_required: "Pick the provider that will run this corridor.",

  // Delete dialog
  delete_title: "Delete this corridor's route",
  delete_semantics: "The route is removed and the corridor is freed to be configured again.",
  delete_consequence: "From then on, {{corridor}} refuses every operation until a new route exists.",
  delete_consequence_blocked:
    "The route is inactive, so {{corridor}} already refuses its operations: deleting it makes nothing worse and is the only way to free the corridor.",
  delete_frees_corridor: "Deleting it is the required first step to point the corridor at another provider.",
  delete_confirm: "Delete route",
  delete_cancel: "Cancel",

  // Change-provider dialog
  switch_title: "Change provider",
  switch_subtitle:
    "The engine allows one route per corridor, so the change deletes the current one and creates the new one.",
  switch_current_label: "Current route",
  switch_gap_warning:
    "Between deleting the current route and creating the new one, the corridor is briefly unrouted: operations landing in that window are refused.",
  switch_no_alternative: "No other integrated provider covers this corridor, so there is nowhere to move it.",
  switch_confirm: "Change provider",
  switch_failed_title: "The corridor was left with no route",
  switch_failed_detail:
    "The previous route was deleted but the new one could not be created, so the corridor is refusing operations. Retry the creation.",
  switch_retry: "Retry the creation",

  // Feedback
  toast_created: "Route created: {{corridor}} is now live.",
  toast_deleted: "Route deleted: {{corridor}} is left with no route.",
  toast_switched: "Provider changed: {{corridor}} now runs on the new one.",
  error_delete_missing: "The route no longer existed. The view has been refreshed.",
  error_no_write_permission: "You do not have permission to change corridors.",
  error_create: "The route could not be created.",
  error_delete: "The route could not be deleted.",
};
